import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Container,
  Stack,
  Alert,
  Chip,
  Fade,
  Slide,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Avatar,
  Badge,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ConfirmationNumber as TicketIcon,
  Search as SearchIcon,
  Event as EventIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  CheckCircle as ValidIcon,
  Error as InvalidIcon,
  Refresh as RefreshIcon,
  Share as ShareIcon,
  Info as InfoIcon,
  WhatsApp as WhatsAppIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Email as EmailIcon,
  ContentCopy as CopyIcon,
  QrCode2 as BarcodeIcon,
  Smartphone as SmartphoneIcon,
} from '@mui/icons-material';
import { supabase } from '../lib/supabaseClient';
import { sanitizeInput } from '../lib/validation';
import JsBarcode from "jsbarcode";
import { useRef } from "react";

interface Ticket {
  serial_number: string;
  date_of_show: string;
  ne_show: string;
  ticket_holder_name: string;
  status?: string;
}

export default function TicketTracking() {
  const [searchQuery, setSearchQuery] = useState('');
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [shareMenuAnchor, setShareMenuAnchor] = useState<null | HTMLElement>(null);
  const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Load search history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('ticket_search_history');
    if (savedHistory) {
      setSearchHistory(JSON.parse(savedHistory));
    }
  }, []);

  // Save search history to localStorage
  const saveToHistory = (query: string) => {
    const newHistory = [query, ...searchHistory.filter(item => item !== query)].slice(0, 5);
    setSearchHistory(newHistory);
    localStorage.setItem('ticket_search_history', JSON.stringify(newHistory));
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setTicket(null);
    const sanitizedQuery = sanitizeInput(searchQuery);
    if (!sanitizedQuery) {
      setError('Please enter a serial number.');
      return;
    }
    setSearchQuery(sanitizedQuery);
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('ne_show_tickets')
        .select('*')
        .eq('serial_number', searchQuery)
        .single();

      if (error) throw error;
      if (data) {
        setTicket(data);
        saveToHistory(sanitizedQuery);
      } else {
        setError('No ticket found with this serial number');
      }
    } catch (err) {
      setError('Error searching for ticket');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleHistoryClick = (historyItem: string) => {
    setSearchQuery(historyItem);
    setShowHistory(false);
  };

  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('ticket_search_history');
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getTicketStatus = () => {
    if (!ticket) return null;
    const showDate = new Date(ticket.date_of_show);
    const now = new Date();
    
    if (showDate < now) {
      return { status: 'completed', label: 'Event Completed', color: 'default' as const };
    } else if (showDate.getTime() - now.getTime() < 24 * 60 * 60 * 1000) {
      return { status: 'upcoming', label: 'Event Today', color: 'warning' as const };
    } else {
      return { status: 'valid', label: 'Valid Ticket', color: 'success' as const };
    }
  };

  const ticketStatus = getTicketStatus();

  // Share functionality
  const handleShareClick = (event: React.MouseEvent<HTMLElement>) => {
    setShareMenuAnchor(event.currentTarget);
  };

  const handleShareClose = () => {
    setShareMenuAnchor(null);
  };

  const generateShareText = () => {
    if (!ticket) return '';
    return `🎫 My Ticket Details\n\nEvent: ${ticket.ne_show}\nDate: ${formatDate(ticket.date_of_show)}\nSerial: ${ticket.serial_number}\nHolder: ${ticket.ticket_holder_name}`;
  };

  const shareToSocialMedia = (platform: string) => {
    const shareText = generateShareText();
    const encodedText = encodeURIComponent(shareText);
    const currentUrl = window.location.href;

    let shareUrl = '';
    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodedText}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}&quote=${encodedText}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodeURIComponent(currentUrl)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=Ticket Details&body=${encodedText}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(shareText);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 500);
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
    handleShareClose();
  };

  const BarcodeSVG = ({ value }: { value: string }) => {
    const svgRef = useRef<SVGSVGElement>(null);

    useEffect(() => {
      if (svgRef.current) {
        JsBarcode(svgRef.current, value, {
          format: "CODE128",
          displayValue: true,
          fontSize: 18,
          height: 80,
          width: 2,
          margin: 0,
        });
      }
    }, [value]);

    return <svg ref={svgRef} />;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column' }}>
      {/* Header with enhanced styling */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ 
          fontWeight: 700,
          color: '#1a237e',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          mb: 2,
        }}>
          <Badge badgeContent={<ValidIcon sx={{ fontSize: 16, color: '#4caf50' }} />}>
            <TicketIcon sx={{ fontSize: 48, color: '#1a237e' }} />
          </Badge>
          Ticket Tracking
        </Typography>
        <Typography variant="h6" sx={{ color: '#666', fontWeight: 400 }}>
          Enter your ticket serial number to view details and verify authenticity
        </Typography>
      </Box>

      {/* Search Section with enhanced UI */}
      <Fade in timeout={800}>
        <Card sx={{ 
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          mb: 3,
        }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ 
              fontWeight: 600,
              color: 'white',
              mb: 3,
              textAlign: 'center',
            }}>
              🔍 Search Your Ticket
            </Typography>
            
            <form onSubmit={handleSearch}>
              <Stack spacing={3}>
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    fullWidth
                    label="Enter Ticket Serial Number"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="e.g., TKT-2024-001234"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: 'rgba(255,255,255,0.95)',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,1)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'white',
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 500,
                      }
                    }}
                    InputProps={{
                      startAdornment: <TicketIcon sx={{ mr: 1, color: '#1a237e' }} />,
                    }}
                  />
                  
                  {/* Search History Dropdown */}
                  {showHistory && searchHistory.length > 0 && (
                    <Paper sx={{ 
                      position: 'absolute', 
                      top: '100%', 
                      left: 0, 
                      right: 0, 
                      zIndex: 1000,
                      mt: 1,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      borderRadius: 2,
                    }}>
                      <Box sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                            Recent Searches
                          </Typography>
                          <IconButton size="small" onClick={clearHistory}>
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Stack spacing={1}>
                          {searchHistory.map((item, index) => (
                            <Chip
                              key={index}
                              label={item}
                              onClick={() => handleHistoryClick(item)}
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: '#f5f5f5' }
                              }}
                              size="small"
                            />
                          ))}
                        </Stack>
                      </Box>
                    </Paper>
                  )}
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  sx={{ 
                    height: 56,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                    boxShadow: '0 6px 20px rgba(33, 150, 243, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1976D2 30%, #1E88E5 90%)',
                      boxShadow: '0 8px 25px rgba(33, 150, 243, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                    '&:disabled': {
                      background: 'linear-gradient(45deg, #ccc 30%, #ddd 90%)',
                    }
                  }}
                >
                  {loading ? 'Searching...' : 'Search Ticket'}
                </Button>
              </Stack>
            </form>

            {/* Help Text */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <InfoIcon fontSize="small" />
                Need help? Contact support for assistance
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Fade>

      {/* Error Alert */}
      {error && (
        <Slide direction="down" in={!!error} timeout={300}>
          <Alert 
            severity="error" 
            sx={{ 
              borderRadius: 3,
              mb: 3,
              boxShadow: '0 4px 12px rgba(244,67,54,0.2)',
            }}
            action={
              <Button color="inherit" size="small" onClick={() => setError(null)}>
                Dismiss
              </Button>
            }
          >
            {error}
          </Alert>
        </Slide>
      )}

      {/* Loading Progress */}
      {loading && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress 
            sx={{ 
              borderRadius: 2,
              height: 6,
              backgroundColor: 'rgba(33, 150, 243, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 2,
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              }
            }} 
          />
        </Box>
      )}

      {/* Ticket Details with enhanced design */}
      {ticket && (
        <Slide direction="up" in={!!ticket} timeout={500}>
          <Card sx={{ 
            borderRadius: 3,
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          }}>
            <CardContent sx={{ p: 4 }}>
              {/* Ticket Header */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ 
                  fontWeight: 700,
                  color: '#1a237e',
                  mb: 2,
                }}>
                  🎫 Ticket Details
                </Typography>
                
                {ticketStatus && (
                  <Chip
                    icon={ticketStatus.status === 'valid' ? <ValidIcon /> : <InvalidIcon />}
                    label={ticketStatus.label}
                    color={ticketStatus.color}
                    sx={{ 
                      fontSize: '1rem',
                      fontWeight: 600,
                      py: 1,
                      px: 2,
                    }}
                  />
                )}
              </Box>

              {/* Ticket Information Grid */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.9)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    border: '2px solid rgba(25, 118, 210, 0.1)',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: '#1976d2', width: 40, height: 40 }}>
                        <TicketIcon />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        Serial Number
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a237e', fontFamily: 'monospace' }}>
                      {ticket.serial_number}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.9)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    border: '2px solid rgba(76, 175, 80, 0.1)',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: '#4caf50', width: 40, height: 40 }}>
                        <EventIcon />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#4caf50' }}>
                        Event
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      {ticket.ne_show}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.9)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    border: '2px solid rgba(255, 152, 0, 0.1)',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: '#ff9800', width: 40, height: 40 }}>
                        <CalendarIcon />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#ff9800' }}>
                        Event Date
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#e65100' }}>
                      {formatDate(ticket.date_of_show)}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.9)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    border: '2px solid rgba(156, 39, 176, 0.1)',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: '#9c27b0', width: 40, height: 40 }}>
                        <PersonIcon />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#9c27b0' }}>
                        Ticket Holder
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#7b1fa2' }}>
                      {ticket.ticket_holder_name}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Ticket Status */}
                <Grid item xs={12}>
                  <Paper sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.9)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    border: `2px solid ${(ticket.status || 'available') === 'used' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)'}`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ 
                        bgcolor: (ticket.status || 'available') === 'used' ? '#f44336' : '#4caf50', 
                        width: 40, 
                        height: 40 
                      }}>
                        {(ticket.status || 'available') === 'used' ? <InvalidIcon /> : <ValidIcon />}
                      </Avatar>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 600, 
                        color: (ticket.status || 'available') === 'used' ? '#f44336' : '#4caf50' 
                      }}>
                        Ticket Status
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      color: (ticket.status || 'available') === 'used' ? '#d32f2f' : '#2e7d32',
                      textTransform: 'capitalize'
                    }}>
                      {(ticket.status || 'available') === 'used' ? 'Used' : 'Available'}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: '#666', 
                      mt: 1,
                      fontStyle: 'italic'
                    }}>
                      {(ticket.status || 'available') === 'used' 
                        ? 'This ticket has been used and cannot be used again.' 
                        : 'This ticket is available for use at the event.'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Action Buttons */}
              <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Tooltip title="Share Ticket">
                  <IconButton 
                    sx={{ 
                      bgcolor: '#4caf50', 
                      color: 'white',
                      '&:hover': { bgcolor: '#388e3c' },
                      width: 56,
                      height: 56,
                    }}
                    onClick={handleShareClick}
                  >
                    <ShareIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="View Barcode">
                  <IconButton 
                    sx={{ 
                      bgcolor: '#ff9800', 
                      color: 'white',
                      '&:hover': { bgcolor: '#f57c00' },
                      width: 56,
                      height: 56,
                    }}
                    onClick={() => setBarcodeDialogOpen(true)}
                  >
                    <BarcodeIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Share Menu */}
              <Menu
                anchorEl={shareMenuAnchor}
                open={Boolean(shareMenuAnchor)}
                onClose={handleShareClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                PaperProps={{
                  sx: {
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    minWidth: 200,
                  }
                }}
              >
                <MenuItem onClick={() => shareToSocialMedia('whatsapp')}>
                  <ListItemIcon>
                    <WhatsAppIcon sx={{ color: '#25D366' }} />
                  </ListItemIcon>
                  <ListItemText primary="WhatsApp" />
                </MenuItem>
                <MenuItem onClick={() => shareToSocialMedia('facebook')}>
                  <ListItemIcon>
                    <FacebookIcon sx={{ color: '#1877F2' }} />
                  </ListItemIcon>
                  <ListItemText primary="Facebook" />
                </MenuItem>
                <MenuItem onClick={() => shareToSocialMedia('twitter')}>
                  <ListItemIcon>
                    <TwitterIcon sx={{ color: '#1DA1F2' }} />
                  </ListItemIcon>
                  <ListItemText primary="Twitter" />
                </MenuItem>
                <MenuItem onClick={() => shareToSocialMedia('email')}>
                  <ListItemIcon>
                    <EmailIcon sx={{ color: '#EA4335' }} />
                  </ListItemIcon>
                  <ListItemText primary="Email" />
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => shareToSocialMedia('copy')}>
                  <ListItemIcon>
                    <CopyIcon sx={{ color: copySuccess ? '#4caf50' : '#666' }} />
                  </ListItemIcon>
                  <ListItemText primary={copySuccess ? "Copied!" : "Copy to Clipboard"} />
                </MenuItem>
              </Menu>

              {/* Barcode Dialog */}
              <Dialog
                open={barcodeDialogOpen}
                onClose={() => setBarcodeDialogOpen(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle sx={{ textAlign: 'center', fontWeight: 600 }}>
                  📊 Ticket Barcode
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{ 
                    p: 3, 
                    bgcolor: 'white', 
                    borderRadius: 2,
                    border: '2px solid #333',
                    display: 'inline-block',
                    minWidth: 320,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333', fontFamily: 'monospace' }}>
                      {ticket.serial_number}
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center',
                      alignItems: 'center',
                      bgcolor: 'white',
                      borderRadius: 1,
                      px: 2,
                      py: 1,
                    }}>
                      <BarcodeSVG value={ticket.serial_number} />
                    </Box>
                    <Typography variant="body2" sx={{ mt: 2, color: '#666', fontWeight: 500 }}>
                      Scan this barcode for quick verification
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 1 }}>
                      Code 128B Format • Compatible with standard barcode scanners
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2, color: '#1a237e', fontWeight: 700, fontFamily: 'monospace', letterSpacing: 1, textAlign: 'center' }}>
                      Serial: {ticket.serial_number}
                    </Typography>
                  </Box>
                  
                  {/* Barcode Information */}
                  <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: '#666', textAlign: 'left' }}>
                      <strong>Barcode Details:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', textAlign: 'left', mt: 1 }}>
                      • Format: Code 128B (Alphanumeric)
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', textAlign: 'left' }}>
                      • Data: {ticket.serial_number}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', textAlign: 'left' }}>
                      • Status: {(ticket.status || 'available') === 'used' ? 'Used' : 'Available'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', textAlign: 'left' }}>
                      • Event: {ticket.ne_show}
                    </Typography>
                  </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                  <Button 
                    onClick={() => setBarcodeDialogOpen(false)}
                    variant="contained"
                    sx={{ borderRadius: 2 }}
                  >
                    Close
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Additional Information */}
              <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a237e', mb: 2 }}>
                  📱 Mobile Ticket Info
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimeIcon fontSize="small" />
                    Please arrive at least 30 minutes before the event
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" />
                    Show this screen or the barcode to event staff for entry
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TicketIcon fontSize="small" />
                    This ticket is non-transferable and non-refundable
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SmartphoneIcon fontSize="small" />
                    Keep your phone charged and screen brightness up for easy scanning
                  </Typography>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Slide>
      )}
    </Container>
  );
} 